import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  createCorrection, createPurchaseRequest, decidePurchaseRequest, decideRefundRequest, listGymCorrections,
  listGymMonthLocks, listGymPurchaseRequests, listGymRefundRequests, searchSalesForRefund,
} from "@/lib/api";
import { CoachIncentiveNote, NewPurchase, NewRefund } from "@/components/accounts/RequestQueue";
import { Empty, TH, TR, errMsg, money } from "@/components/accounts/shared";

// A gym's requests to accounts: refunds, purchases, and corrections to a month accounts have closed.
export default function Requests() {
  const [refunds, setRefunds] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [corrections, setCorrections] = useState([]);
  const [locks, setLocks] = useState([]);
  const [open, setOpen] = useState(null);
  const load = useCallback(async () => {
    try {
      const [r, p, c, l] = await Promise.all([listGymRefundRequests(), listGymPurchaseRequests(), listGymCorrections(), listGymMonthLocks()]);
      setRefunds(r); setPurchases(p); setCorrections(c); setLocks(l);
    } catch (e) { toast.error(errMsg(e, "Could not load requests")); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const decide = async (kind, r, approve) => {
    const note = approve ? "" : window.prompt("Why is it rejected?");
    if (!approve && !note) return;
    try { await (kind === "refund" ? decideRefundRequest : decidePurchaseRequest)(r.id, { approve, note }); load(); }
    catch (e) { toast.error(errMsg(e, "Could not save")); }
  };
  const done = () => { setOpen(null); load(); };

  const table = (kind, rows) => (
    rows.length === 0 ? <Empty>None yet.</Empty> : (
      <table className="w-full text-sm min-w-[640px]">
        <thead><tr className={TH}><th className="px-3 py-2 font-medium">Date</th><th className="px-3 py-2 font-medium">{kind === "refund" ? "Member" : "Item"}</th>
          <th className="px-3 py-2 font-medium">Reason</th><th className="px-3 py-2 font-medium text-right">Amount</th><th className="px-3 py-2 font-medium">Status</th><th /></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className={TR}>
              <td className="px-3 py-2 text-xs text-zinc-400">{String(r.requested_at).slice(0, 10)}<div className="text-[10px] text-zinc-500">{r.requested_by}</div></td>
              <td className="px-3 py-2 text-zinc-200">{kind === "refund" ? r.member : r.item}{kind === "refund" && r.can_approve && <CoachIncentiveNote r={r} />}</td>
              <td className="px-3 py-2 text-xs text-zinc-400">{r.reason}{r.decision_note && <div className="italic text-zinc-500">{r.decision_note}</div>}</td>
              <td className="px-3 py-2 text-right tabular-nums">{money(r, r.paid_amount || r.amount)}<div className="text-[10px] text-zinc-500">needs {r.approval_level_label}</div></td>
              <td className="px-3 py-2 text-xs text-zinc-400">{r.status}{r.paid_date && <div className="text-[10px] text-zinc-500">paid {r.paid_date}</div>}</td>
              <td className="px-3 py-2 text-right whitespace-nowrap">{r.can_approve && <>
                <Button size="sm" variant="ghost" onClick={() => decide(kind, r, true)} className="h-7 text-emerald-300"><Check className="h-3.5 w-3.5 mr-1" />Approve</Button>
                <Button size="sm" variant="ghost" onClick={() => decide(kind, r, false)} className="h-7 text-rose-300"><X className="h-3.5 w-3.5" /></Button></>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    )
  );

  const section = (title, hint, action, body) => (
    <div className="card-surface overflow-x-auto">
      <div className="px-4 py-3 flex items-center justify-between gap-2">
        <div><div className="text-sm font-semibold text-zinc-100">{title}</div><div className="text-[11px] text-zinc-500">{hint}</div></div>
        {action}
      </div>
      {body}
    </div>
  );
  const add = (k, label, disabled) => (
    <Button size="sm" disabled={disabled} onClick={() => setOpen(k)} className="bg-blue-600 hover:bg-blue-500 h-8" data-testid={`gym-new-${k}`}><Plus className="h-3.5 w-3.5 mr-1" /> {label}</Button>
  );

  return (
    <div className="space-y-5 max-w-5xl" data-testid="requests-page">
      <div>
        <div className="eyebrow">This gym</div>
        <h1 className="text-2xl font-semibold text-zinc-50">Requests to accounts</h1>
        <p className="text-xs text-zinc-500 mt-1">Small amounts the facility head approves; larger ones go to accounts, the largest to a master admin. Accounts pay and record them.</p>
      </div>
      {section("Refunds", "Money back to a member, on one of this gym's sales.", add("refund", "Request refund"), table("refund", refunds))}
      {section("Purchases", "Equipment, supplies, repairs. Attach a quote if you have one.", add("purchase", "Request purchase"), table("purchase", purchases))}
      {section("Corrections to a closed month", locks.length ? `Closed: ${locks.map((l) => l.month).sort().join(", ")}` : "No month is closed yet.",
        add("correction", "Request correction", !locks.length),
        corrections.length === 0 ? <Empty>None yet.</Empty> : (
          <table className="w-full text-sm"><tbody>
            {corrections.map((c) => (
              <tr key={c.id} className={TR}>
                <td className="px-3 py-2 text-xs text-zinc-400 w-28">{c.month}</td>
                <td className="px-3 py-2 text-xs"><div className="text-zinc-200">{c.what}</div><div className="text-zinc-500">{c.member ? `${c.member} · ` : ""}{c.why}</div></td>
                <td className="px-3 py-2 text-xs text-zinc-400">{c.status}{c.open_until && <div className="text-[10px] text-zinc-500">change it before {c.open_until.slice(0, 16).replace("T", " ")}</div>}{c.decision_note && <div className="italic text-zinc-500">{c.decision_note}</div>}</td>
              </tr>
            ))}
          </tbody></table>
        ))}
      {open === "refund" && <NewRefund onClose={() => setOpen(null)} onDone={done} />}
      {open === "purchase" && <NewPurchase create={createPurchaseRequest} onClose={() => setOpen(null)} onDone={done} />}
      {open === "correction" && <NewCorrection locks={locks} onClose={() => setOpen(null)} onDone={done} />}
    </div>
  );
}

function NewCorrection({ locks, onClose, onDone }) {
  const months = [...new Set(locks.map((l) => l.month))].sort().reverse();
  const [f, setF] = useState({ month: months[0] || "", what: "", why: "", sale_id: null, record_type: "sale" });
  const [q, setQ] = useState("");
  const [hits, setHits] = useState([]);
  const [sale, setSale] = useState(null);
  useEffect(() => {
    if (q.trim().length < 2) { setHits([]); return undefined; }
    const t = setTimeout(() => searchSalesForRefund(q).then((r) => setHits(r.filter((h) => String(h.date).slice(0, 7) === f.month))).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q, f.month]);
  const save = async () => {
    try {
      await createCorrection({ ...f, sale_id: sale?.id || null, record_type: sale ? "sale" : "missing" });
      toast.success("Sent to accounts");
      onDone();
    } catch (e) { toast.error(errMsg(e, "Could not send")); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100 max-w-md">
        <DialogHeader><DialogTitle>Correction to a closed month</DialogTitle></DialogHeader>
        <div className="space-y-2 text-sm">
          <select value={f.month} onChange={(e) => setF({ ...f, month: e.target.value })} className="w-full bg-[#0a0a0a] border border-[#262626] rounded-md h-9 px-2">
            {months.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          {sale ? (
            <div className="text-zinc-300">{sale.member_name} <span className="text-xs text-zinc-500">{String(sale.date).slice(0, 10)} · {sale.programme}</span>
              <button onClick={() => setSale(null)} className="ml-2 text-xs text-blue-300">change</button></div>
          ) : (
            <>
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Which sale? (member name) — leave blank if a record is missing" className="bg-[#0a0a0a] border-[#262626]" />
              {hits.map((h) => <button key={h.id} onClick={() => setSale(h)} className="block w-full text-left px-2 py-1 hover:bg-[#1a1a1a] rounded text-xs">{h.member_name} · {String(h.date).slice(0, 10)} · {h.programme}</button>)}
            </>
          )}
          <Input value={f.what} onChange={(e) => setF({ ...f, what: e.target.value })} placeholder="What needs to change" className="bg-[#0a0a0a] border-[#262626]" />
          <Input value={f.why} onChange={(e) => setF({ ...f, why: e.target.value })} placeholder="Why" className="bg-[#0a0a0a] border-[#262626]" />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={!f.month || !f.what.trim() || !f.why.trim()} className="bg-blue-600 hover:bg-blue-500">Send</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
